import {assert} from 'Asserts';
import {
  BasicItem,
  CapabilityFeature,
  CapabilityFeatureDefinition,
  INTENTIONALLY_SKIPPED_FEATURE,
  isFeatureWithPermissionMapping,
  isFeatureWithPermissionMappingDefinition,
  isItemWithPermissions,
  isStandardFeatureDefinition,
  isStandardFeatureDefinitionWithItem,
  ItemWithPermissions,
  PermissionsLookupSupportedStandardFeatures,
  StandardFeatureDefinition,
  UNMAPPED_STANDARD_FEATURE_TO_PERMISSION_KEY,
} from 'auth/capabilities/CapabilityFeatureConstants';
import {
  isFeatureEnabledFromFeatureList,
  isFeatureEnabledFromPermissions,
  isFeatureEnabledFromShareLink,
  legacyIsFeatureEnabled,
} from 'auth/capabilities/CapabilityFeatureEnablement';
import {makeMissingCapabilityUserMessage} from 'auth/capabilities/CapabilityFeatureUserMessages';
import {getCapabilityFeatures} from 'auth/capabilities/getCapabilityFeatures';
import {isFeatureInDevelopment} from 'auth/capabilities/InDevelopmentCapabilities';
import {FeatureDefinition} from 'auth/feature-enablement/FeatureConstants';
import {DisabledReason, FeatureStatus, makeFeatureStatus} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';

function shouldUseLegacyGating(feature: CapabilityFeature): boolean {
  if (!BenchlingConfig.IDENTITY_ENABLE_CAPABILITY_GATING) {
    return true;
  }
  return (
    isFeatureInDevelopment(feature) &&
    !BenchlingConfig.IDENTITY_ENABLE_GATING_AND_PROVISIONING_IN_DEVELOPMENT_CAPABILITIES
  );
}

function getCapabilityFeatureStatus(
  featureDefinition: CapabilityFeatureDefinition,
  isSharedContext: boolean = false
): FeatureStatus {
  if (shouldUseLegacyGating(featureDefinition.feature)) {
    return makeFeatureStatus(
      legacyIsFeatureEnabled(featureDefinition, isSharedContext),
      DisabledReason.LEGACY
    );
  }

  // If the feature is being accessed for an item opened via sharelink we bypass normal capability management
  // checks for features defined to be "share link" available.
  if (isFeatureEnabledFromShareLink(featureDefinition)) {
    return {enabled: true};
  }

  // If we are passed an item with a permissions dict, we defer to its value which will have the capability
  // feature status composed in.
  if (isFeatureWithPermissionMappingDefinition(featureDefinition) && featureDefinition.item?.permissions) {
    return makeFeatureStatus(
      isFeatureEnabledFromPermissions(featureDefinition.feature, featureDefinition.item),
      DisabledReason.INSUFFICIENT_PERMISSION
    );
  }

  const userMessage = makeMissingCapabilityUserMessage([featureDefinition.feature]);
  return makeFeatureStatus(
    isFeatureEnabledFromFeatureList(featureDefinition.feature),
    DisabledReason.MISSING_CAPABILITY,
    userMessage
  );
}

function getItemIfExists(featureDef: StandardFeatureDefinition): BasicItem | ItemWithPermissions | null {
  if (isStandardFeatureDefinitionWithItem(featureDef)) {
    return featureDef.item;
  }
  return null;
}

function getItemIfHasPermissions(
  item: BasicItem | ItemWithPermissions | null
): ItemWithPermissions | undefined {
  return item && isItemWithPermissions(item) ? item : undefined;
}

function translateIntoCapabilityDefinitions(
  standardFeatureDefinition: StandardFeatureDefinition,
  capabilityFeatures: Array<CapabilityFeature>
): Array<CapabilityFeatureDefinition> {
  const item = getItemIfExists(standardFeatureDefinition);

  return capabilityFeatures.map((capabilityFeature) => {
    if (isFeatureWithPermissionMapping(capabilityFeature)) {
      return {
        feature: capabilityFeature,
        item: getItemIfHasPermissions(item),
      };
    }
    return {
      feature: capabilityFeature,
      ...(item ? {item} : {}),
    };
  });
}

function getUnmappedStandardFeatureStatus(featureDefinition: StandardFeatureDefinition): FeatureStatus {
  const maybeItem = getItemIfExists(featureDefinition);
  const itemWithPermissions = getItemIfHasPermissions(maybeItem);
  if (!itemWithPermissions) {
    // Return true if we are passed an ungated application item type without a permissions object
    return {enabled: true};
  }

  assert(
    Object.keys(UNMAPPED_STANDARD_FEATURE_TO_PERMISSION_KEY).includes(featureDefinition.feature),
    'StandardFeature does not support permissions lookup',
    {feature: featureDefinition.feature}
  );

  const permissionsKey =
    UNMAPPED_STANDARD_FEATURE_TO_PERMISSION_KEY[
      featureDefinition.feature as PermissionsLookupSupportedStandardFeatures
    ];

  // @ts-expect-error TODO: resolve type safety between Permissions and PermissionsGraphQL
  if (permissionsKey === INTENTIONALLY_SKIPPED_FEATURE || itemWithPermissions.permissions[permissionsKey]) {
    return {enabled: true};
  }
  return {enabled: false, reason: DisabledReason.INSUFFICIENT_PERMISSION};
}

function getStandardFeatureStatus(featureDefinition: StandardFeatureDefinition): FeatureStatus {
  const capabilityFeatures = getCapabilityFeatures(featureDefinition);

  if (capabilityFeatures == null) {
    return getUnmappedStandardFeatureStatus(featureDefinition);
  }

  const capabilityFeatureDefinitions = translateIntoCapabilityDefinitions(
    featureDefinition,
    capabilityFeatures
  );

  const listStatus = getCapabilityFeatureListStatus(capabilityFeatureDefinitions, true);
  return makeFeatureStatus(
    !listStatus.allDisabled,
    DisabledReason.MISSING_CAPABILITY,
    listStatus.userMessage
  );
}

export function getCapabilityManagementFeatureStatus(featureDefinition: FeatureDefinition): FeatureStatus {
  if (isStandardFeatureDefinition(featureDefinition)) {
    return getStandardFeatureStatus(featureDefinition);
  }
  return getCapabilityFeatureStatus(featureDefinition as CapabilityFeatureDefinition);
}

export interface FeatureListStatus {
  allDisabled: boolean;
  userMessage?: string;
}

/**
 * Determines the status of a list of capability features. Notably, the API is optimized for checking if ANY
 * feature is enabled instead of if ALL features in the list are enabled because that is the most common use
 * case.
 *
 * @param featureDefinitions The capability feature definitions to determine the status of
 * @param isSharedContext If the feature being checked is shared by multiple applications (i.e. was a
 * StandardFeature used for the gate)
 * @returns The status of the list
 */
export function getCapabilityFeatureListStatus(
  featureDefinitions: Array<CapabilityFeatureDefinition>,
  isSharedContext: boolean = false
): FeatureListStatus {
  let allDisabled = true;

  const featuresMissingCapabilities = [];
  for (const featureDefinition of featureDefinitions) {
    const status = getCapabilityFeatureStatus(featureDefinition, isSharedContext);
    if (status.enabled) {
      allDisabled = false;
    } else if (status.reason === DisabledReason.MISSING_CAPABILITY) {
      featuresMissingCapabilities.push(featureDefinition);
    }
  }
  if (allDisabled && featuresMissingCapabilities.length) {
    return {
      allDisabled,
      userMessage: makeMissingCapabilityUserMessage(featuresMissingCapabilities.map((fd) => fd.feature)),
    };
  }
  return {allDisabled};
}
